import {
	type Api,
	type Transformation,
	type TransformUsages,
	transformUsages,
} from './transformer';
import { getImportSpecifiers, lowerFirst } from './utils';

type LoaderOptimization = 'keep' | 'noop';

export type Priority = 'background' | 'interaction' | 'userVisible';

export type Optimizer = {
	loader?: Partial<Record<Priority, LoaderOptimization>>;
};

export type TransformOptions = {
	optimizer?: Optimizer;
	source: TransformUsages['source'];
} & (
	| {
			getModule(request: string): Promise<{ moduleId: string; moduleName?: string }>;
	  }
	| {
			/** @deprecated */
			getModuleId(request: string): Promise<string>;
	  }
);

export type { Transformation };

export function transform({
	optimizer,
	source,
	...opts
}: TransformOptions): Promise<Transformation> | Transformation {
	const specifiers = getImportSpecifiers(source, '@atlassian/react-async');
	const apis: Api[] = Array.from(specifiers)
		.filter(
			([imported]) => imported.startsWith('importFor') || imported.startsWith('JSResourceFor'),
		)
		.flatMap(([imported, locals]) =>
			Array.from(locals, (local) => ({
				imported,
				local,
				optimize({ code, loader }) {
					const priority = lowerFirst(imported.replace(/importFor|JSResourceFor/, '')) as Priority;

					const optimization = optimizer?.loader?.[priority] ?? 'keep';
					if (optimization === 'noop') {
						return code.replace(loader, '() => new Promise(() => {})');
					}

					return code;
				},
			})),
		);

	if (!apis.length) {
		// Return the source as nothing was imported from @atlassian/react-async that should be transformed
		return {
			code: source,
			meta: {
				jsResources: [],
			},
		};
	}

	return transformUsages({
		apis,
		source,
		...opts,
	});
}
